import { CircleDot, Loader2, RefreshCw, Server } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

interface HarnessWorker {
  id: string;
  worker_id: string;
  name: string;
  status: "online" | "busy" | "offline";
  available: boolean;
  last_seen_at: string | null;
  capabilities: Record<string, unknown>;
  current_job_id: string | null;
  team_id: number | null;
}

const REFRESH_MS = 15_000;

function StatusBadge({ status, available }: Readonly<{ status: string; available: boolean }>) {
  const map: Record<string, { dot: string; text: string; label: string }> = {
    online: { dot: "text-green-400", text: "text-green-400", label: available ? "Online · Available" : "Online" },
    busy: { dot: "text-amber-400", text: "text-amber-400", label: "Busy" },
    offline: { dot: "text-content-tertiary", text: "text-content-tertiary", label: "Offline" },
  };
  const s = map[status] ?? map.offline;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.text}`}>
      <CircleDot className={`h-3 w-3 ${s.dot}`} />
      {s.label}
    </span>
  );
}

function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function WorkerRow({ worker }: Readonly<{ worker: HarnessWorker }>) {
  return (
    <div className="flex items-center justify-between p-3 bg-surface-secondary border border-border-secondary rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content-primary">{worker.name}</span>
          <code className="text-xs text-content-tertiary font-mono truncate">{worker.worker_id}</code>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-content-tertiary">
          <span>Last seen: {relativeTime(worker.last_seen_at)}</span>
          {typeof worker.capabilities?.harness_version === "string" && (
            <span>v{worker.capabilities.harness_version as string}</span>
          )}
          {worker.capabilities?.opencode === true && <span className="text-green-400">opencode ✓</span>}
          {worker.current_job_id && <span>Job: {worker.current_job_id}</span>}
        </div>
      </div>
      <StatusBadge status={worker.status} available={worker.available} />
    </div>
  );
}

export function HarnessWorkersManager() {
  const [workers, setWorkers] = useState<HarnessWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkers = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const data = await api.get<{ data: HarnessWorker[] }>("/harness/workers");
      setWorkers(data.data);
      setError(null);
    } catch {
      setError("Failed to load harness workers");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkers();
    const id = setInterval(() => fetchWorkers(), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchWorkers]);

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <Server className="h-5 w-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-content-primary">RALLY — Remote Access Link &amp; Loopback Yoke</h3>
            <p className="text-sm text-content-tertiary">
              Machines connected to run agentic work. Status updates automatically.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchWorkers(true)}
          className="flex items-center gap-2 px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <WorkerList loading={loading} workers={workers} />
    </div>
  );
}

function WorkerList({ loading, workers }: Readonly<{ loading: boolean; workers: HarnessWorker[] }>) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-content-tertiary" />
      </div>
    );
  }

  if (workers.length === 0) {
    return (
      <p className="text-sm text-content-tertiary py-4">
        No RALLY machines connected yet. Create an API key with the{" "}
        <span className="text-content-secondary">harness</span> scope (Security → API Keys) and install the RALLY
        connector on a machine to see it here.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {workers.map((w) => (
        <WorkerRow key={w.id} worker={w} />
      ))}
    </div>
  );
}
